import { binaryCodec, decodeFrame } from './binary-codec.js';
import type { ClientMessage, ServerMessage } from './messages.js';

/**
 * THE ENCODE/DECODE SEAM.
 *
 * WebSocket replication is the real scaling bottleneck for this game: 100 players x 20 Hz x ~20
 * visible entities each is far more bytes than simulation is CPU. JSON is what the protocol was
 * written against because it is debuggable; `binary-codec.ts` is what it ships on, and the swap
 * is `setProtocolCodec(...)` with no call site moving.
 *
 * Nothing outside these two files may call `JSON.stringify` / `JSON.parse` on a protocol message.
 * Every call site goes through `encodeClientMessage` / `encodeServerMessage` /
 * `parseClientMessage` / `parseServerMessage`.
 *
 * ONLY THE ENCODER IS SWITCHED. Both codecs decode through `decodeFrame`, which reads either
 * format — see the note in `binary-codec.ts`. That is what makes `PROTOCOL_CODEC=json` an
 * operator's debugging tool rather than a coordinated redeploy: one process can be dropped to
 * readable frames while its peer keeps speaking binary.
 */
export type WireData = string | ArrayBuffer | Uint8Array;

export interface ProtocolCodec {
  readonly name: string;
  encode(message: ClientMessage | ServerMessage): WireData;
  /**
   * Returns the raw decoded value. Validation is deliberately NOT the codec's job — a codec that
   * also validated would have to be rewritten alongside every schema change.
   */
  decode(data: WireData): unknown;
}

export const jsonCodec: ProtocolCodec = {
  name: 'json',
  encode(message) {
    return JSON.stringify(message);
  },
  decode: decodeFrame,
};

/** The names `PROTOCOL_CODEC` / `VITE_PROTOCOL_CODEC` accept. */
export type ProtocolCodecName = 'binary' | 'json';

/**
 * Which codec a process should encode with, from its environment.
 *
 * Binary is the default and the unset value, because a deployment that quietly fell back to JSON
 * would be one paying ~4x the bytes with nothing in the logs to say so. `json` is the escape
 * hatch: it is what makes a bad frame readable at 3am, and it is safe to set on one side alone.
 * Anything else is a typo, and a typo should not silently change the wire format.
 */
export function resolveProtocolCodec(name: string | undefined): ProtocolCodec {
  return name?.trim().toLowerCase() === 'json' ? jsonCodec : binaryCodec;
}

let activeCodec: ProtocolCodec = binaryCodec;

export function getProtocolCodec(): ProtocolCodec {
  return activeCodec;
}

/** Swap the wire format process-wide. Both ends must agree, so this is tied to PROTOCOL_VERSION. */
export function setProtocolCodec(codec: ProtocolCodec): void {
  activeCodec = codec;
}

export function encode(message: ClientMessage | ServerMessage): WireData {
  return activeCodec.encode(message);
}

export function decode(data: WireData): unknown {
  return activeCodec.decode(data);
}
